import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { useTranslation } from '@/lib/i18n';
import { cn, formatNumber, campaignImageSize } from '@worldcoo/ui/lib/utils';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CheckIcon, ListFilterIcon, SearchIcon } from 'lucide-react';
import { Suspense } from 'react';
import { Input } from '@worldcoo/ui/components/input';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { NotFoundIcon } from './assets/not-found-icon';
import { StateFilter } from './campaigns-filters-sidebar';
import { Image } from '../../-components/image';
import {
	usePortalAuthorQuery,
	usePortalCampaignsQuery,
	usePortalMetadataQuery,
	usePortalCampaignsAggregatesQuery,
} from '@/lib/queries';
import { Progress } from '@worldcoo/ui/components/progress';
import { useSidebar } from '@worldcoo/ui/components/sidebar';
import { useIsFilterApplied } from '@/lib/use-filter-applied';
import { usePortalContext } from '@/lib/use-portal-context';
import { isNotFound } from '@tanstack/react-router';

export function Campaigns() {
	return (
		<Suspense
			fallback={
				<div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
					{Array.from({ length: 9 }).map((_, index) => (
						// oxlint-disable-next-line react/no-array-index-key
						<Skeleton key={index} className="shadow-3xl h-96 rounded-xl" />
					))}
				</div>
			}
		>
			<SuspensedCampaigns />
		</Suspense>
	);
}

function SuspensedCampaigns() {
	const { i18n } = useTranslation();
	const navigate = useNavigate({ from: '/$portalId' });
	const { q } = useSearch({ from: '/$portalId/' });
	const sidebar = useSidebar();

	const isFilterApplied = useIsFilterApplied();
	const data = usePortalCampaignsQuery();

	if (isNotFound(data)) return null;

	const { campaigns, campaignsCount, activeCampaignsCount } = data;
	const finishedCampaignsCount = campaignsCount - activeCampaignsCount;
	const showNgos = useShowNgos(campaigns);

	return (
		<>
			<div className="flex flex-col justify-between gap-6">
				<div className="flex flex-col gap-3">
					<h2 className="text-4xl font-medium">{i18n.portal.campaigns.title}</h2>
					<p>{i18n.portal.campaigns.description}</p>
				</div>

				{activeCampaignsCount + finishedCampaignsCount > 1 && (
					<DivWithPrimaryColorVariable className="flex flex-col-reverse items-center justify-between gap-3 lg:flex-row">
						<StateFilter />

						<div className="z-10 flex w-full gap-3 lg:w-auto">
							<div className="relative w-full lg:w-auto">
								<SearchIcon className="text-muted-foreground pointer-events-none absolute m-1.5 p-0.5" />
								<Input
									className="bg-background! rounded-sm pl-10 shadow-none lg:max-w-60"
									placeholder={i18n.actions.search}
									value={q}
									onChange={(e) => navigate({ search: { q: e.target.value }, resetScroll: false })}
								/>
							</div>

							<Button
								variant={isFilterApplied ? 'outlinePrimary' : 'outline'}
								size="icon"
								className="bg-background! hover:bg-accent! relative cursor-pointer rounded-sm shadow-none"
								onClick={() => sidebar.toggleSidebar()}
							>
								<ListFilterIcon />
								<span className="sr-only">{i18n.actions.filter}</span>

								{isFilterApplied && (
									<div className="border-background absolute -top-1 -right-1 size-3 rounded-full border bg-red-500" />
								)}
							</Button>
						</div>
					</DivWithPrimaryColorVariable>
				)}
			</div>

			<DivWithPrimaryColorVariable>
				<ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
					{campaigns.length === 0 && (
						<div className="bg-background shadow-3xl col-span-full flex w-full flex-col items-center justify-center gap-6 rounded-xl p-6">
							<NotFoundIcon className="text-primary" />
							<span className="text-lg font-medium">{i18n.portal.campaigns.notFound}</span>
						</div>
					)}
					{campaigns.map((campaign) => (
						<CampaignCard key={campaign.id} campaign={campaign} showNgos={showNgos} />
					))}
				</ul>
			</DivWithPrimaryColorVariable>
		</>
	);
}

function useShowNgos(campaigns: Array<{ ngos: Array<{ id: string }> }>) {
	const author = usePortalAuthorQuery();
	if (isNotFound(author)) return true;
	if (author.type !== 'ngo') return true;
	return !campaigns.every((c) => c.ngos.length === 1 && c.ngos[0]?.id === author.ngoId);
}

function CampaignCard(props: {
	campaign: {
		id: string;
		name: string | undefined;
		heroImageAlt?: string;
		description?: string;
		media?: { location: string };
		finished: boolean;
		ngos: Array<{ id: string; name: string | undefined; logo?: { location: string; width: number; height: number } }>;
	};
	showNgos: boolean;
}) {
	const { i18n } = useTranslation();
	const { portalId } = usePortalContext();
	const data = usePortalCampaignsQuery();

	if (isNotFound(data)) return null;

	return (
		<li
			className={cn(
				'bg-background shadow-3xl relative flex flex-col gap-3 rounded-xl p-3',
				data.campaigns.length === 1 && 'col-span-full p-6 lg:flex-row lg:gap-10 lg:pr-10',
			)}
		>
			<Link
				to="/$portalId/$campaignId"
				params={{ portalId, campaignId: props.campaign.id }}
				search={(prev) => prev}
				className={cn('flex flex-col gap-3', data.campaigns.length === 1 && 'flex-1')}
			>
				<div className="relative overflow-hidden rounded-lg">
					{props.campaign.media && (
						<Image
							src={props.campaign.media.location}
							height={data.campaigns.length === 1 ? 336 : campaignImageSize.height}
							aspectRatio={data.campaigns.length === 1 ? 796 / 336 : campaignImageSize.width / campaignImageSize.height}
							alt={props.campaign.heroImageAlt ?? props.campaign.name}
							className={cn(
								'w-full min-w-full object-cover',
								data.campaigns.length === 1 ? 'aspect-796/336 h-[336px]' : campaignImageSize.className,
							)}
							loading="lazy"
						/>
					)}
					<CampaignCardBadge campaignId={props.campaign.id} finished={props.campaign.finished} />
				</div>
			</Link>

			<div className={cn('flex flex-col gap-4', data.campaigns.length === 1 ? 'py-4 lg:w-1/3' : 'flex-1')}>
				<Link
					to="/$portalId/$campaignId"
					params={{ portalId, campaignId: props.campaign.id }}
					search={(prev) => prev}
					className="mb-auto flex flex-col gap-3"
				>
					<h3 className="text-lg leading-snug">{props.campaign.name}</h3>

					{props.campaign.description && (
						<div
							className={cn(
								'text-muted-foreground **:text-muted-foreground! **:font-normal! text-sm',
								data.campaigns.length === 1 && 'text-base',
							)}
							dangerouslySetInnerHTML={{ __html: props.campaign.description }}
						/>
					)}
				</Link>

				{/**
				 * MARK: NGOs list
				 **/}
				{portalId !== 'eroski' && props.showNgos && (
					<div className="mb-3 flex flex-col gap-1">
						<span>{i18n.portal.aCampaignFrom.replace('{{ngoName}}', '')}</span>
						<div className="flex h-[50px] items-center gap-3">
							{props.campaign.ngos
								.filter((ngo) => ngo.logo !== undefined)
								.map((ngo) => (
									<Image
										key={ngo.id}
										src={ngo.logo!.location}
										height={50}
										aspectRatio={ngo.logo!.width / ngo.logo!.height}
										alt={ngo.name}
										fetchPriority="low"
										loading="lazy"
									/>
								))}
						</div>
					</div>
				)}

				<CampaignCardProgress campaignId={props.campaign.id} />

				<Link
					to={!props.campaign.finished ? '/$portalId/$campaignId/donate' : '/$portalId/$campaignId'}
					params={{ portalId, campaignId: props.campaign.id }}
					search={(prev) => prev}
					className={cn(
						buttonVariants({ size: 'xl' }),
						props.campaign.finished && 'text-primary bg-primary/10 hover:bg-primary/7',
					)}
				>
					{!props.campaign.finished ? i18n.actions.donate : i18n.actions.viewCampaign}
				</Link>
			</div>
		</li>
	);
}

function CampaignCardBadge(props: { campaignId: string; finished: boolean }) {
	return (
		<Suspense fallback={<CampaignCardBadgeFallback finished={props.finished} />}>
			<SuspensedCampaignCardBadge campaignId={props.campaignId} finished={props.finished} />
		</Suspense>
	);
}

function CampaignCardBadgeFallback(props: { finished: boolean }) {
	const { i18n } = useTranslation();

	if (!props.finished) return null;

	return (
		<div className="bg-background absolute top-3 left-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm">
			<CheckIcon className="text-primary-foreground size-5 rounded-full bg-green-600 p-1" strokeWidth={4} />
			{i18n.portal.campaignFunded}
		</div>
	);
}

function SuspensedCampaignCardBadge(props: { campaignId: string; finished: boolean }) {
	const { i18n } = useTranslation();
	const { locale } = usePortalContext();
	const aggregates = usePortalCampaignsAggregatesQuery();

	if (!aggregates || !('aggregates' in aggregates)) return null;

	const campaignAggregate = aggregates.aggregates[props.campaignId];
	if (!campaignAggregate) return null;

	const progress = campaignAggregate.progress ?? 0;
	const funding = campaignAggregate.funding;

	return (
		<>
			{props.finished && (
				<div className="bg-background absolute top-3 left-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm">
					<CheckIcon className="text-primary-foreground size-5 rounded-full bg-green-600 p-1" strokeWidth={4} />
					{i18n.portal.campaignFunded}
				</div>
			)}

			{!props.finished && funding && (
				<div className="bg-background absolute top-3 left-3 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm">
					{i18n.portal.percentageFunded.replace('{{percentage}}', formatNumber(locale, progress, 0))}
				</div>
			)}
		</>
	);
}

function CampaignCardProgress(props: { campaignId: string }) {
	return (
		<Suspense fallback={<CampaignCardProgressFallback />}>
			<SuspensedCampaignCardProgress campaignId={props.campaignId} />
		</Suspense>
	);
}

function CampaignCardProgressFallback() {
	return (
		<div className="flex flex-col gap-1">
			<Skeleton className="h-1.5 w-full" />
			<div className="flex items-center gap-2">
				<Skeleton className="h-7 w-20" />
				<Skeleton className="h-5 w-32" />
			</div>
		</div>
	);
}

function SuspensedCampaignCardProgress(props: { campaignId: string }) {
	const { i18n } = useTranslation();
	const { locale } = usePortalContext();
	const metadata = usePortalMetadataQuery();
	const aggregates = usePortalCampaignsAggregatesQuery();

	if (isNotFound(metadata)) return null;
	if (!aggregates || !('aggregates' in aggregates)) return null;
	if (metadata.hideCollected) return null;

	const campaignAggregate = aggregates.aggregates[props.campaignId];
	if (!campaignAggregate) return null;

	if (metadata.hideStatsOnActiveCampaigns && !campaignAggregate.finished) {
		return null;
	}

	const finished = campaignAggregate.finished;
	const progress = campaignAggregate.progress ?? 0;
	const collected = campaignAggregate.collected ?? 0;
	const funding = campaignAggregate.funding;

	if (!funding) return null;

	return (
		<div className="flex flex-col gap-1">
			{!finished && collected < funding && <Progress value={progress} className="h-1.5" />}
			<div className="flex items-center gap-2">
				<span className="text-primary text-lg font-semibold">{formatNumber(locale, collected) + ' €'}</span>
				<span className="text-muted-foreground text-sm">
					{i18n.statistics.collectedFromBudget.replace('{{budget}}', formatNumber(locale, funding, 0)) + ' €'}
				</span>
			</div>
		</div>
	);
}
